import _ from "lodash";
import cn from "classnames";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Button, Col, Container, Row } from "reactstrap";
import {
  CARD_CHECK_LOADING,
  PLAN_COUPON_LOADING,
  PLAN_LOADING,
  UPGRADE_PLAN_LOADING,
} from "../../constants/loading.constant";
import { usePaymentCore } from "../../cores/payment";
import { useMeCore } from "../../cores/me";
import { usePlanCore } from "../../cores/plan";
import { PlanType } from "../../cores/plan/contants/planType.constant";
import { PlanBadge } from "../component/companies/PlanBadge";
import { InputWembii } from "../component/InputWembii";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { PlanRule } from "../component/plan/PlanRule";
import { CouponInfo } from "../component/plan/CouponInfo";
import { Plan } from "../../cores/plan/interfaces/api/get-plans";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import AreYouSure from "../component/AreYouSure";
import { CHANGE_SUBSCRIPTION_MODAL } from "../../constants/modal.constant";
import { CL$Format, parseChileMoneyString } from "../../cores/utils";
import { useIntl } from "react-intl";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../styles/Plans.scss";

/** Códigos de moneda (API); la etiqueta visible va por i18n (p. ej. CLP → «Pesos chilenos»). */
const PLAN_CURRENCIES = [
  { id: "CLP", labelId: "app.plansPage.currencyCLP" },
  { id: "USD", labelId: "app.plansPage.currencyUSD" },
  { id: "BRL", labelId: "app.plansPage.currencyBRL" },
  { id: "PEN", labelId: "app.plansPage.currencyPEN" },
] as const;

export const Plans = () => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const [couponTxt, setCouponTxt] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [checkedCoupon, setCheckedCoupon] = useState(false);
  const [payWithBank, setPayWithBank] = useState(false);
  const [cardValid] = useState(false);
  const [coin, setCoin] = useState("CLP");
  const [type, setType] = useState("monthly");
  const { getAllPlans, getPlans, plans } = usePlanCore();
  const { goToMeCompany } = useRouterCore();
  const { reset, turnOffModal, turnOnModal, isLoading } = useGlobalCore();
  const {
    postFlowSubscription,
    deleteSubscription,
    getFlowCoupon,
    getStripeCoupon,
    postStripeSubscription,
    resetCoupon,
    coupon,
  } = usePaymentCore();
  const {
    userPlan,
    companyPlan,
    companyId,
    companyRole,
    deselectCompany,
    isAdmin,
    isReseller,
  } = useMeCore();

  /** Plan de facturación de la compañía seleccionada (owner); si no hay, el del usuario. */
  const billingPlan = useMemo(() => {
    if (companyId != null && companyPlan != null) return companyPlan;
    return userPlan ?? null;
  }, [companyId, companyPlan, userPlan]);

  /** Catálogo sin FREE. Dedupe solo por `name` cuando es no vacío; si no, una clave por id (evita colapsar todo el catálogo). */
  const displayPlans = useMemo(() => {
    const paid = plans.filter((p) => p.name !== PlanType.FREE);
    const preferredId = billingPlan?.id;
    const tierKey = (p: Plan) =>
      p.name && String(p.name).trim() !== ""
        ? String(p.name)
        : `__id:${p.id}`;

    const byKey = new Map<string, Plan>();
    for (const p of paid) {
      const key = tierKey(p);
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, p);
      } else if (p.id === preferredId) {
        byKey.set(key, p);
      } else if (prev.id !== preferredId && p.id < prev.id) {
        byKey.set(key, p);
      }
    }
    const seen = new Set<number>();
    const ordered: Plan[] = [];
    for (const p of paid) {
      const canonical = byKey.get(tierKey(p))!;
      if (!seen.has(canonical.id)) {
        seen.add(canonical.id);
        ordered.push(canonical);
      }
    }
    return ordered;
  }, [plans, billingPlan?.id]);

  const formatPlanPrice = useCallback(
    (plan: Plan | null) => {
      if (!plan) return "—";
      let priceVal: string;
      switch (coin) {
        case "CLP":
          if (payWithBank && cardValid) {
            priceVal =
              type === "annual"
                ? plan.annualFlowChileBankPrice!
                : plan.flowChileBankPrice!;
          } else {
            priceVal =
              type === "annual" ? plan.annualFlowPrice! : plan.flowPrice!;
          }
          {
            // Monto en pesos tal cual lo envía el API (sin multiplicador en front).
            const amount = parseChileMoneyString(priceVal);
            if (!Number.isFinite(amount)) return "—";
            const prefix = intl.formatMessage({
              id: "app.plansPage.planPricePrefixCLP",
            });
            return CL$Format(amount, "", prefix);
          }
        case "USD":
          priceVal =
            type === "annual"
              ? plan.annualStripeUsdPrice!
              : plan.stripeUsdPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return "—";
            return `USD ${n}`;
          }
        case "BRL":
          priceVal =
            type === "annual"
              ? plan.annualStripeBrlPrice!
              : plan.stripeBrlPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return "—";
            return `BRL ${n}`;
          }
        case "PEN":
          priceVal =
            type === "annual"
              ? plan.annualStripePenPrice!
              : plan.stripePenPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return "—";
            return `PEN ${n}`;
          }
        default:
          return "—";
      }
    },
    [coin, type, payWithBank, cardValid, intl]
  );

  const price = useMemo(
    () => formatPlanPrice(selectedPlan),
    [formatPlanPrice, selectedPlan]
  );

  const checkoutContextLine = useMemo(() => {
    const billing = intl.formatMessage({
      id: type === "annual" ? "app.plan.annual" : "app.plan.monthly",
    });
    const currencyLabel = intl.formatMessage({
      id:
        PLAN_CURRENCIES.find((c) => c.id === coin)?.labelId ??
        "app.plansPage.currencyCLP",
    });
    return intl.formatMessage(
      { id: "app.plansPage.checkoutContext" },
      { billing, currency: currencyLabel }
    );
  }, [intl, type, coin]);

  const featuredPlanId = useMemo(() => {
    if (displayPlans.length < 3) return null;
    return displayPlans[Math.floor(displayPlans.length / 2)]?.id ?? null;
  }, [displayPlans]);

  useEffect(() => {
    // plans/all solo admin/reseller; el resto usa GET plans/ (evita 403 y estado vacío).
    if (isAdmin || isReseller) {
      getAllPlans();
    } else {
      getPlans();
    }
    resetCoupon();
  }, [
    getAllPlans,
    getPlans,
    resetCoupon,
    billingPlan,
    isAdmin,
    isReseller,
  ]);

  const toggle = (tab: number) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setSelectedPlan(plans.find(({ id }) => id === tab) || null);
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent, planId: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle(planId);
    }
  };

  const handleClickUpgrade = useCallback(async () => {
    const couponId = payWithBank ? null : coupon?.id || null;
    let urlData;
    switch (coin) {
      case "CLP":
        urlData = await postFlowSubscription(
          payWithBank && cardValid
            ? type === "annual"
              ? selectedPlan?.annualFlowChileBankId!
              : selectedPlan?.flowChileBankId!
            : type === "annual"
              ? selectedPlan?.annualFlowId!
              : selectedPlan?.flowId!,
          couponId
        );
        break;
      case "USD":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripeUsdId!
            : selectedPlan?.stripeUsdId!,
          couponId
        );
        break;
      case "BRL":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripeBrlId!
            : selectedPlan?.stripeBrlId!,
          couponId
        );
        break;
      case "PEN":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripePenId!
            : selectedPlan?.stripePenId!,
          couponId
        );
        break;
    }

    if (urlData) window.open(urlData.url, "_self");
  }, [
    postFlowSubscription,
    postStripeSubscription,
    coupon,
    type,
    selectedPlan,
    coin,
    cardValid,
    payWithBank,
  ]);

  const handleClickCancel = useCallback(() => {
    turnOnModal(
      CHANGE_SUBSCRIPTION_MODAL,
      <AreYouSure
        message={`Está seguro de querer cancelar la subscripción activa? Se pasará a tener el Plan FREE`}
        onSubmit={() => {
          deleteSubscription(() => {
            reset();
            deselectCompany();
            goToMeCompany();
          });
          turnOffModal(CHANGE_SUBSCRIPTION_MODAL);
        }}
        onClose={() => turnOffModal(CHANGE_SUBSCRIPTION_MODAL)}
      />
    );
  }, [
    turnOnModal,
    turnOffModal,
    deleteSubscription,
    reset,
    goToMeCompany,
    deselectCompany,
  ]);

  const debouncedCheckCoupon = useRef(
    _.debounce(
      (couponIn, coinIn) =>
        couponIn &&
        (coinIn === "CLP"
          ? getFlowCoupon(couponIn, () => {
              setCheckedCoupon(true);
            })
          : getStripeCoupon(couponIn, () => {
              setCheckedCoupon(true);
            })),
      1000
    )
  ).current;

  useEffect(() => {
    setCheckedCoupon(false);
    debouncedCheckCoupon(couponTxt, coin);
  }, [debouncedCheckCoupon, couponTxt, coin]);

  useEffect(() => {
    if (!billingPlan) return;
    const match = plans.find(({ id }) => id === billingPlan.id) || null;
    if (match?.name === PlanType.FREE) {
      const firstPaid = displayPlans[0];
      setActiveTab(firstPaid?.id ?? 0);
      setSelectedPlan(firstPaid ?? null);
    } else {
      setActiveTab(billingPlan.id);
      setSelectedPlan(match);
    }
  }, [billingPlan, plans, displayPlans, setSelectedPlan]);

  const showUpgradeOptions = useMemo(
    () =>
      (!companyRole || companyRole.owner) &&
      selectedPlan?.name !== PlanType.FREE &&
      billingPlan?.id !== selectedPlan?.id,
    [selectedPlan, billingPlan, companyRole]
  );

  const showCancelOptions = useMemo(
    () =>
      (!companyRole || companyRole.owner) &&
      selectedPlan?.name !== PlanType.FREE &&
      billingPlan?.id === selectedPlan?.id,
    [selectedPlan, billingPlan, companyRole]
  );

  const handleChangeType = useCallback(
    (next: string) => {
      resetCoupon();
      setPayWithBank(false);
      setType(next);
    },
    [resetCoupon]
  );

  const handleChangeCoin = useCallback(
    (next: string) => {
      resetCoupon();
      setPayWithBank(false);
      setCoin(next);
    },
    [resetCoupon]
  );

  return (
    <Layout>
      <Content>
        <div className="plans-page">
          <Container fluid className="plans-page__inner position-relative">
            <UpdateLoadingCard
              className="position-absolute-center"
              loadingId={PLAN_LOADING}
              text={intl.formatMessage({ id: "app.plansPage.loadingPlans" })}
            />

            {!isLoading(PLAN_LOADING) && (
              <>
                <header className="plans-page__hero">
                  <h1 className="plans-page__title">
                    {intl.formatMessage({ id: "app.planModal.title" })}
                  </h1>
                  <p className="plans-page__subtitle">
                    {intl.formatMessage({ id: "app.plansPage.subtitle" })}
                  </p>

                  <div className="plans-page__toolbar">
                    <div
                      className="plans-billing-toggle"
                      role="group"
                      aria-label={intl.formatMessage({
                        id: "app.plansPage.billing",
                      })}
                    >
                      <button
                        type="button"
                        className={cn("plans-billing-toggle__btn", {
                          "plans-billing-toggle__btn--active":
                            type === "monthly",
                        })}
                        onClick={() => handleChangeType("monthly")}
                        aria-pressed={type === "monthly"}
                      >
                        {intl.formatMessage({ id: "app.plan.monthly" })}
                      </button>
                      <button
                        type="button"
                        className={cn("plans-billing-toggle__btn", {
                          "plans-billing-toggle__btn--active": type === "annual",
                        })}
                        onClick={() => handleChangeType("annual")}
                        aria-pressed={type === "annual"}
                      >
                        {intl.formatMessage({ id: "app.plan.annual" })}
                      </button>
                    </div>

                    <div
                      className="plans-currency"
                      role="group"
                      aria-label={intl.formatMessage({
                        id: "app.plansPage.currency",
                      })}
                    >
                      <span className="plans-currency__label">
                        {intl.formatMessage({ id: "app.plansPage.currency" })}
                      </span>
                      {PLAN_CURRENCIES.map(({ id, labelId }) => (
                        <button
                          key={id}
                          type="button"
                          className={cn("plans-currency__chip", {
                            "plans-currency__chip--active": coin === id,
                          })}
                          onClick={() => handleChangeCoin(id)}
                          aria-pressed={coin === id}
                        >
                          {intl.formatMessage({ id: labelId })}
                        </button>
                      ))}
                    </div>
                  </div>
                </header>

                <div className="plans-grid">
                  {displayPlans.map((plan) => {
                    const isSelected = activeTab === plan.id;
                    const isCurrent = billingPlan?.id === plan.id;
                    const isFeatured =
                      featuredPlanId !== null && plan.id === featuredPlanId;

                    return (
                      <article
                        key={plan.id}
                        role="button"
                        tabIndex={0}
                        className={cn("plans-card", {
                          "plans-card--selected": isSelected,
                        })}
                        onClick={() => toggle(plan.id)}
                        onKeyDown={(e) => handleCardKeyDown(e, plan.id)}
                        aria-current={isSelected ? "true" : undefined}
                      >
                        <div className="plans-card__badges">
                          {isCurrent && (
                            <span className="plans-card__badge-current">
                              {intl.formatMessage({
                                id: "app.plansPage.yourPlan",
                              })}
                            </span>
                          )}
                          {isFeatured && !isCurrent && (
                            <span className="plans-card__badge-featured">
                              {intl.formatMessage({
                                id: "app.plansPage.featured",
                              })}
                            </span>
                          )}
                          <PlanBadge plan={plan} show />
                        </div>

                        <div className="plans-card__price-block">
                          <div className="plans-card__price">
                            {formatPlanPrice(plan)}
                          </div>
                          <span className="plans-card__price-meta">
                            {type === "annual"
                              ? intl.formatMessage({
                                  id: "app.plansPage.perYear",
                                })
                              : intl.formatMessage({
                                  id: "app.plansPage.perMonth",
                                })}
                            {coin === "CLP" && (
                              <>
                                {" "}
                                · {intl.formatMessage({ id: "app.plan.iva" })}
                              </>
                            )}
                          </span>
                        </div>

                        <div className="plans-card__features">
                          {Object.keys(plan.rules).map((key) => (
                            <PlanRule
                              key={key}
                              name={key}
                              value={String(
                                (plan.rules as unknown as Record<
                                  string,
                                  string | number | boolean
                                >)[key]
                              )}
                              compact
                            />
                          ))}
                        </div>
                      </article>
                    );
                  })}
                </div>

                <UpdateLoadingCard
                  loadingId={UPGRADE_PLAN_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.changingPlan" })}
                />
                <UpdateLoadingCard
                  loadingId={PLAN_COUPON_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.searchingCoupon" })}
                />
                <UpdateLoadingCard
                  loadingId={CARD_CHECK_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.checkingCard" })}
                />

                {(showUpgradeOptions || showCancelOptions) && selectedPlan && (
                  <div
                    className="plans-checkout"
                    aria-labelledby="plans-checkout-heading"
                  >
                    <div className="plans-checkout__top">
                      <span className="plans-checkout__eyebrow">
                        {showUpgradeOptions
                          ? intl.formatMessage({
                              id: "app.plansPage.checkoutEyebrowUpgrade",
                            })
                          : intl.formatMessage({
                              id: "app.plansPage.checkoutEyebrowCurrent",
                            })}
                      </span>
                      <h2
                        id="plans-checkout-heading"
                        className="plans-checkout__title"
                      >
                        {intl.formatMessage(
                          {
                            id: showUpgradeOptions
                              ? "app.plansPage.checkoutTitleUpgrade"
                              : "app.plansPage.checkoutTitleCurrent",
                          },
                          {
                            plan: intl.formatMessage({
                              id: selectedPlan.name,
                            }),
                          }
                        )}
                      </h2>
                      <p className="plans-checkout__context">
                        {checkoutContextLine}
                      </p>
                    </div>

                    <div className="plans-checkout__price-block">
                      <div className="plans-checkout__price-label">
                        {intl.formatMessage({
                          id: showUpgradeOptions
                            ? "app.plansPage.checkoutPriceLabelUpgrade"
                            : "app.plansPage.checkoutPriceLabelCurrent",
                        })}
                      </div>
                      <div className="plans-checkout__price-value">{price}</div>
                      <p className="plans-checkout__help">
                        {intl.formatMessage({
                          id: showUpgradeOptions
                            ? "app.plansPage.checkoutHelpUpgrade"
                            : "app.plansPage.checkoutHelpCurrent",
                        })}
                      </p>
                    </div>

                    {showUpgradeOptions && (
                      <div className="plans-checkout__actions">
                        {!payWithBank && (
                          <Row className="w-100 mb-2">
                            <Col xs="12" md="8">
                              <InputWembii
                                label={intl.formatMessage({
                                  id: "app.plansPage.couponLabel",
                                })}
                                value={couponTxt}
                                mb={false}
                                onChange={setCouponTxt}
                              />
                            </Col>
                            <Col
                              xs="12"
                              md="4"
                              className="d-flex align-items-end justify-content-md-end pt-2 pt-md-0"
                            >
                              {checkedCoupon && <CouponInfo coupon={coupon} />}
                            </Col>
                          </Row>
                        )}
                        <Button
                          type="button"
                          className="plans-btn-primary"
                          onClick={handleClickUpgrade}
                        >
                          {intl.formatMessage({ id: "app.plan.subscribe" })}
                        </Button>
                      </div>
                    )}

                    {showCancelOptions && (
                      <div className="plans-checkout__actions">
                        <Button
                          type="button"
                          className="plans-btn-ghost"
                          onClick={handleClickCancel}
                        >
                          {intl.formatMessage({ id: "app.plan.cancel" })}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Container>
        </div>
      </Content>
    </Layout>
  );
};
